import React, { useMemo, useState } from "react";
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Bar,
  Legend,
  ComposedChart,
  Line,
} from "recharts";
import {
  Zap,
  TrendingDown,
  AlertCircle,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  HousePlug,
  AlertTriangle,
  Lightbulb,
  ArrowUpCircle,
  ArrowDownCircle,
  Filter,
  IndianRupee,
  Gauge,
  Flame,
  ShieldAlert,
} from "lucide-react";
import { useSelector } from "react-redux";
import { Card } from "@mui/material";

// --- Shadcn UI Select Imports ---
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// --- Updated Data ---
const ENERGY_KPI_DATA = {
  totalKwhToday: "1,842.5",
  kwhPerM3: "0.78",
  worstPump: "Pump-07 (Zone B)",
  deviationPercent: "+35%",
};

const CHART_DATA = [
  { time: "06:00", flow: 45, energy: 35, specificEnergy: 0.77 },
  { time: "09:00", flow: 52, energy: 48, specificEnergy: 0.92 },
  { time: "12:00", flow: 60, energy: 45, specificEnergy: 0.75 },
  { time: "15:00", flow: 58, energy: 55, specificEnergy: 0.94 },
  { time: "18:00", flow: 65, energy: 50, specificEnergy: 0.76 },
  { time: "21:00", flow: 50, energy: 42, specificEnergy: 0.84 },
];

const ENERGY_RECORDS = [
  {
    asset: "PUMP-02",
    location: "Narwal plain 2",
    siteType: "Tubewell",
    flow: "50,705",
    head: "37.97",
    requiredKw: "39.46",
    actualKw: "76.68",
    extraKwHr: "37.22",
    extraKwYear: "2,71,737.11",
    annualLoss: "1902159.79",
    waste: "48.5%",
    status: "Critical",
  },
  {
    asset: "PUMP-01",
    location: "Narwal plain 1",
    siteType: "Tubewell",
    flow: "48,301",
    head: "58.51",
    requiredKw: "57.92",
    actualKw: "88.95",
    extraKwHr: "31.03",
    extraKwYear: "2,26,534.05",
    annualLoss: "1585738.37",
    waste: "34.9%",
    status: "Warning",
  },
];

const PUMP_COMPARISON_DATA = [
  {
    id: "PUMP-01",
    specs: "48,301 GPH @ 58.51m head",
    requiredKw: "57.92",
    actualKw: "88.95",
    efficiency: "65.1%",
    extraKwHour: "31.03",
    extraKwYear: "2,26,534.05",
    annualLoss: "15,85,738.37",
    status: "warning",
    recommendation:
      "High waste: Schedule maintenance. Verify operating point vs design curve.",
  },
  {
    id: "PUMP-02",
    specs: "50,705 GPH @ 37.97m head",
    requiredKw: "39.46",
    actualKw: "76.68",
    efficiency: "51.5%",
    extraKwHour: "37.22",
    extraKwYear: "2,71,737.11",
    annualLoss: "19,02,159.79",
    status: "critical",
    recommendation:
      "Critical: Inspect impeller immediately. Check for wear, cavitation, or system mismatch.",
  },
];

const Badge = ({ children, className }) => (
  <span className={`px-2 py-1 rounded text-[10px] uppercase ${className}`}>
    {children}
  </span>
);

const EnergyEfficiencyDashboard = () => {
  const themeMode = useSelector((state) => state.theme.mode);

  // Filter States
  const [siteTypeFilter, setSiteTypeFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [headRangeFilter, setHeadRangeFilter] = useState("all");

  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
  const cardColor = themeMode ? "bg_light_main" : "bg-white";
  const borderCol = themeMode ? "border_them_2" : "border-primary";
  const cardBg = themeMode ? "bg_light_main" : "bg-white";
  const tableHedBg = themeMode ? "table_hed_bg" : "bg-slate-50";

  // --- FILTER LOGIC ---
  const filteredRecords = useMemo(() => {
    return ENERGY_RECORDS.filter((item) => {
      const siteMatch =
        siteTypeFilter === "all" ||
        item.siteType.toLowerCase() === siteTypeFilter.toLowerCase();
      const locMatch =
        locationFilter === "all" ||
        item.location.toLowerCase() === locationFilter.toLowerCase();

      let headMatch = true;
      const headVal = parseFloat(item.head);
      if (headRangeFilter === "low") headMatch = headVal < 40;
      else if (headRangeFilter === "high") headMatch = headVal >= 40;

      return siteMatch && locMatch && headMatch;
    });
  }, [siteTypeFilter, locationFilter, headRangeFilter]);

  return (
    <div className={`w-full ${bgColorClass} h-full ${textColorClass}`}>
      {/* Header */}
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center gap-x-2`}
        >
          <HousePlug size={16} />
          <p className="font-semibold">Energy & Efficiency Analytics</p>
        </div>
      </div>

      <div className="h-[calc(100vh-50px)] pb-10 sidebar_scroll overflow-y-auto">
        <div className="px-3 md:px-5 lg:px-12 pt-6">
          {/* Primary KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {/* 1. Total kWh Today */}
            <KPICard
              label="Total kWh Today"
              value={ENERGY_KPI_DATA.totalKwhToday}
              unit="kWh"
              trend="+4.2%"
              isUp={true}
              icon={<Zap size={22} />}
              color="text-amber-500"
              cardColor={cardColor}
              borderThem={borderThem}
            />

            {/* 2. Specific Energy */}
            <KPICard
              label="Specific Energy"
              value={ENERGY_KPI_DATA.kwhPerM3}
              unit="kWh/m³"
              trend="-2.1%"
              isUp={false}
              icon={<Gauge size={22} />}
              color="text-blue-500"
              cardColor={cardColor}
              borderThem={borderThem}
            />

            {/* 3. Worst Performing - Enhanced Alert Design */}
            <div
              className={`relative overflow-hidden rounded-2xl border-2 border-red-500/30 p-3 shadow-lg ${cardColor}`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="bg-red-500 text-white p-1.5 rounded-lg shadow-sm">
                  <ShieldAlert size={18} />
                </div>
                <Badge className="bg-red-500 text-white animate-pulse">
                  Critical
                </Badge>
              </div>
              <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest">
                Worst Performing
              </p>
              <h3 className="text-base font-black mt-1 leading-tight">
                {ENERGY_KPI_DATA.worstPump}
              </h3>
              <p className="text-[11px] mt-2 opacity-70 flex items-center gap-1">
                <TrendingDown size={12} className="text-red-500" />
                {ENERGY_KPI_DATA.deviationPercent} over design limit
              </p>
            </div>

            {/* 4. Total Extra kW / Year */}
            <div
              className={`group relative overflow-hidden rounded-2xl border ${borderCol} p-3 ${cardBg} hover:shadow-md transition-all`}
            >
              <div className="relative z-10">
                <div className="bg-orange-500/10 text-orange-600 w-fit p-2 rounded-xl mb-3">
                  <Flame size={20} />
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-60">
                  Extra kW / Year
                </p>
                <h2 className="text-xl font-black text-orange-600 tracking-tighter">
                  4,98,271.16
                </h2>
              </div>
              <Activity
                size={70}
                className="absolute -right-4 -bottom-4 text-orange-500 opacity-[0.07] group-hover:scale-110 transition-transform"
              />
            </div>

            {/* 5. Total Annual Loss */}
            <div
              className={`group relative overflow-hidden rounded-2xl border ${borderCol} p-3 ${cardBg} hover:shadow-md transition-all`}
            >
              <div className="relative z-10">
                <div className="bg-emerald-500/10 text-emerald-600 w-fit p-2 rounded-xl mb-3">
                  <IndianRupee size={20} />
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-60">
                  Annual Loss
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xs font-bold text-emerald-600">₹</span>
                  <h2 className="text-xl font-black text-emerald-600 tracking-tighter">
                    ₹ 34,87,898.16
                  </h2>
                </div>
              </div>
              <TrendingDown
                size={70}
                className="absolute -right-4 -bottom-4 text-emerald-500 opacity-[0.07] group-hover:scale-110 transition-transform"
              />
            </div>
          </div>

          <div className="mb-3">
            <h3 className="text-base font-semibold">
              Required vs Actual Impact
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {PUMP_COMPARISON_DATA.map((pump, idx) => (
              <PumpComparisonCard
                key={idx}
                data={pump}
                cardColor={cardColor}
                borderThem={borderThem}
                themeMode={themeMode}
              />
            ))}
          </div>

          {/* Table Section */}
          <div className="mb-4">
            <h3 className="text-base font-semibold">
              Required vs Actual consumption analysis
            </h3>
          </div>

          <div className={`mb-3 flex flex-wrap gap-6 items-center`}>
            {/* Site Type Filter */}
            <div className="flex flex-col gap-1.5">
              <Select value={siteTypeFilter} onValueChange={setSiteTypeFilter}>
                <SelectTrigger
                  className={`w-[140px] text-xs h-9 border ${borderCol} ${cardBg} focus:ring-1 focus:ring-blue-500`}
                >
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent
                  className={`${cardBg} ${textColorClass} border ${borderCol}`}
                >
                  <SelectItem value="all">All Sites</SelectItem>
                  <SelectItem value="tubewell">Tubewell</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location Filter */}
            <div className="flex flex-col gap-1.5">
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger
                  className={`w-[180px] text-xs h-9 border ${borderCol} ${cardBg} focus:ring-1 focus:ring-blue-500`}
                >
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent
                  className={`${cardBg} ${textColorClass} border ${borderCol}`}
                >
                  <SelectItem value="all">All Locations</SelectItem>
                  <SelectItem value="narwal plain 1">Narwal Plain 1</SelectItem>
                  <SelectItem value="narwal plain 2">Narwal Plain 2</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Head Range Filter */}
            <div className="flex flex-col gap-1.5">
              <Select
                value={headRangeFilter}
                onValueChange={setHeadRangeFilter}
              >
                <SelectTrigger
                  className={`w-[140px] text-xs h-9 border ${borderCol} ${cardBg} focus:ring-1 focus:ring-blue-500`}
                >
                  <SelectValue placeholder="All Head" />
                </SelectTrigger>
                <SelectContent
                  className={`${cardBg} ${textColorClass} border ${borderCol}`}
                >
                  <SelectItem value="all">All Head</SelectItem>
                  <SelectItem value="low">Low (&lt; 40m)</SelectItem>
                  <SelectItem value="high">High (&ge; 40m)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div
            className={`overflow-x-auto border rounded-2xl ${borderCol} mb-8`}
          >
            <table className="w-full text-left border-collapse">
              <thead
                className={`${tableHedBg} text-[9px] uppercase font-black tracking-widest`}
              >
                <tr>
                  <th className="p-5">Asset Detail</th>
                  <th className="p-5 text-center">Site Type</th>
                  <th className="p-5 text-center">Required kW</th>
                  <th className="p-5 text-center">Actual kW</th>
                  <th className="p-5 text-center">Extra kW/Year</th>
                  <th className="p-5 text-center text-yellow-500">
                    Annual Loss
                  </th>
                  <th className="p-5 text-right">Operational Status</th>
                </tr>
              </thead>
              <tbody className="text-[11px] font-bold">
                {filteredRecords.length > 0 ? (
                  filteredRecords.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-t ${borderCol} hover:bg-blue-500/5 transition-colors`}
                    >
                      <td className="p-5">
                        <p className="text-xs font-black tracking-tight">
                          {row.asset}
                        </p>
                        <p className="text-[9px] opacity-40 uppercase">
                          {row.location} | {row.siteType}
                        </p>
                      </td>
                      <td className="p-5 text-centerfont-mono tracking-tighter italic">
                        {row.siteType}
                      </td>
                      <td className="p-5 text-center text-green-500">
                        <div className="flex items-center justify-center gap-1.5">
                          <ArrowDownCircle size={14} /> {row.requiredKw}
                        </div>
                      </td>
                      <td className="p-5 text-center text-red-500">
                        <div className="flex items-center justify-center gap-1.5">
                          <ArrowUpCircle size={14} /> {row.actualKw}
                        </div>
                      </td>
                      <td className="p-5 text-center text-red-500 font-mono tracking-tighter italic">
                        {row.extraKwYear}
                      </td>
                      <td className="p-5 text-center text-yellow-500 text-xs font-black tracking-tight">
                        ₹{" "}
                        {parseFloat(row.annualLoss).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="p-5 text-right">
                        <Badge
                          className={
                            row.status === "Critical"
                              ? "bg-red-500 text-white font-black"
                              : "bg-orange-500 text-white font-black"
                          }
                        >
                          {row.waste} WASTE
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="p-10 text-center opacity-50 italic"
                    >
                      No assets match the selected audit filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Chart Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-5">
            <div
              className={`lg:col-span-2 border ${borderThem} rounded-2xl p-4 shadow-sm ${cardColor}`}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <Activity size={18} className="text-blue-500" /> Energy vs
                  Flow Correlation
                </h3>
              </div>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={CHART_DATA}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      strokeOpacity={0.1}
                    />
                    <XAxis
                      dataKey="time"
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis fontSize={12} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend verticalAlign="top" height={40} />
                    <Area
                      type="monotone"
                      dataKey="flow"
                      fill="#3b82f6"
                      fillOpacity={0.1}
                      stroke="#3b82f6"
                      strokeWidth={3}
                      name="Flow (m³/h)"
                    />
                    <Bar
                      dataKey="energy"
                      barSize={20}
                      fill="#f59e0b"
                      radius={[4, 4, 0, 0]}
                      name="Energy (kWh)"
                    />
                    <Line
                      type="monotone"
                      dataKey="specificEnergy"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="kWh/m³ Index"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-600 p-5 rounded-2xl text-white shadow-xl">
                <h3 className="font-semibold text-xs tracking-widest mb-4 opacity-80 uppercase">
                  Insight Audit
                </h3>
                <div className="flex items-center gap-4 mb-4 bg-white/10 p-3 rounded-xl border border-white/20">
                  <Search size={24} className="opacity-50" />
                  <div>
                    <p className="text-xs font-bold opacity-70 uppercase">
                      Analysis
                    </p>
                    <p className="text-lg font-semibold leading-tight">
                      Pump-07 Audit
                    </p>
                  </div>
                </div>
                <p className="text-sm font-medium leading-relaxed italic border-l-2 border-white/40 pl-4">
                  "Pump-07 is consuming{" "}
                  <span className="underline decoration-yellow-400 font-black">
                    35% more energy
                  </span>{" "}
                  per m³."
                </p>
                <button className="w-full mt-6 bg-white text-blue-600 py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-slate-100 transition-all">
                  Schedule Inspection
                </button>
              </div>

              <div
                className={`border ${borderThem} rounded-2xl p-4 shadow-sm ${cardColor}`}
              >
                <h3 className="font-semibold text-xs tracking-widest mb-6 opacity-50 uppercase">
                  Efficiency Alerts
                </h3>
                <div className="space-y-4">
                  <AlertItem
                    label="System Variance"
                    value="12%"
                    sub="Actual vs Design"
                    icon={<Activity size={14} />}
                  />
                  <AlertItem
                    label="High kW Draw"
                    value="92.4"
                    sub="Over Threshold"
                    icon={<Zap size={14} />}
                    color="text-red-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Sub Components ---
const PumpComparisonCard = ({ data, cardColor, borderThem, themeMode }) => {
  const statusColor =
    data.status === "critical" ? "text-red-500" : "text-orange-500";
  const statusBorder =
    data.status === "critical" ? "border-red-500/40" : "border-orange-500/40";

  return (
    <div
      className={`border ${borderThem} ${cardColor} rounded-2xl p-5 shadow-sm relative overflow-hidden transition-all hover:border-blue-500/30`}
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h4 className="text-lg font-black tracking-tight">{data.id}</h4>
          <p className="text-[10px] font-bold opacity-40 uppercase tracking-widest">
            {data.specs}
          </p>
        </div>
        <div className={`p-2 rounded-xl bg-current/5 ${statusColor}`}>
          <AlertTriangle size={18} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-3 bg-slate-500/5 rounded-xl border border-transparent hover:border-green-500/20 transition-all">
          <p className="text-[10px] font-black opacity-40 uppercase mb-1">
            Required kW
          </p>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-black text-green-500">
              {data.requiredKw}
            </span>
            <span className="text-[10px] font-bold opacity-30">kW</span>
          </div>
        </div>
        <div className="p-3 bg-slate-500/5 rounded-xl border border-transparent hover:border-red-500/20 transition-all">
          <p className="text-[10px] font-black opacity-40 uppercase mb-1 text-right">
            Actual kW
          </p>
          <div className="flex items-baseline justify-end gap-1">
            <span className="text-2xl font-black text-red-500">
              {data.actualKw}
            </span>
            <span className="text-[10px] font-bold opacity-30">kW</span>
          </div>
        </div>
      </div>

      <div className="space-y-2 border-t border-b border-slate-500/10 py-4 mb-4">
        <div className="flex justify-between text-xs font-bold">
          <span className="opacity-50 uppercase tracking-tighter">
            Current Efficiency
          </span>
          <span>{data.efficiency}</span>
        </div>
        <div className="flex justify-between text-xs font-bold">
          <span className="opacity-50 uppercase tracking-tighter text-orange-500">
            Extra kW / Hour
          </span>
          <span className="text-orange-500">+{data.extraKwHour}</span>
        </div>
        <div className="flex justify-between text-xs font-bold">
          <span className="opacity-50 uppercase tracking-tighter text-red-500">
            Extra kW / Year
          </span>
          <span className="text-red-500">+{data.extraKwYear}</span>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <span className="text-[10px] font-black uppercase opacity-40">
          Annual Financial Loss
        </span>
        <span className="text-xl font-black text-yellow-500">
          ₹{data.annualLoss}
        </span>
      </div>

      <div
        className={`p-3 rounded-xl ${
          themeMode ? "bg-white/5" : "bg-slate-50"
        } border-l-4 ${statusBorder}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <Lightbulb size={12} className="text-yellow-500" />
          <span className="text-[9px] font-black uppercase tracking-widest opacity-60">
            Recommendation
          </span>
        </div>
        <p className="text-xs font-medium leading-relaxed italic opacity-80">
          {data.recommendation}
        </p>
      </div>
    </div>
  );
};

const KPICard = ({
  label,
  value,
  unit,
  trend,
  isUp,
  icon,
  color,
  cardColor,
  borderThem,
}) => (
  <div
    className={`border ${borderThem} p-3 rounded-2xl shadow-sm group hover:border-blue-500/50 transition-all ${cardColor}`}
  >
    <div className="flex justify-between items-center mb-4">
      <div className={`${color} bg-current/10 p-2.5 rounded-xl`}>{icon}</div>
      <div
        className={`flex items-center gap-1 text-[10px] font-black uppercase ${
          isUp ? "text-red-500" : "text-green-500"
        }`}
      >
        {isUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{" "}
        {trend}
      </div>
    </div>
    <div className="flex items-baseline gap-1 mb-1">
      <p className="text-xl font-black tracking-tight">{value}</p>
      <p className="text-xs font-bold opacity-40">{unit}</p>
    </div>
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
      {label}
    </p>
  </div>
);

const AlertItem = ({ label, value, sub, icon, color = "text-blue-500" }) => (
  <div className="flex justify-between items-center p-3 rounded-xl bg-slate-500/5 group hover:bg-slate-500/10 transition-all">
    <div className="flex items-center gap-3">
      <div className={`${color} opacity-40 group-hover:opacity-100`}>
        {icon}
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-tighter mb-1">
          {label}
        </p>
        <p className="text-[9px] opacity-40 italic font-medium">{sub}</p>
      </div>
    </div>
    <span className={`font-black text-sm tracking-tighter ${color}`}>
      {value}
    </span>
  </div>
);

export default EnergyEfficiencyDashboard;
